import { LogLevel } from './log-level';

/** The names `parseLogLevel` accepts, for error messages. */
const VALID_NAMES = Object.keys(LogLevel).filter(k => Number.isNaN(Number(k)));

/**
 * Parses a configured severity name.
 *
 * **An unrecognized name is reported, not silently replaced.** Every local environment in this
 * repository was configured `log_level: 'TRACE'` — not a member of {@link LogLevel} — so
 * `LogLevel['TRACE']` was `undefined` and a `??` fallback quietly substituted `WARNING`. Local
 * development ran three levels quieter than intended for a long time, and nothing said so.
 *
 * It still falls back, because a bad value should not stop an app from booting; it just says so
 * first. `console.error` rather than the logger itself, since this runs while the logger is being
 * configured.
 */
export function parseLogLevel(value: string | undefined, fallback: LogLevel, settingName: string): LogLevel {
    if (value === undefined || value === '') {
        return fallback;
    }

    // `typeof !== 'number'`, NOT `=== undefined`. `LogLevel` is a numeric enum, so it carries
    // reverse mappings: `LogLevel['3']` is the *string* `'WARNING'`. An `undefined` check passes
    // that through as a `LogLevel`, every `level >= floor` comparison then coerces to NaN and
    // returns false, and the app logs NOTHING — no console, no telemetry, no warning. That is a
    // worse failure than the `'TRACE'` one above, reached by writing `'3'` in an environment file.
    const level = LogLevel[value.toUpperCase() as keyof typeof LogLevel];
    if (typeof level !== 'number') {
        console.error(
            `[logging] Unrecognized ${settingName} "${value}" — expected one of ${VALID_NAMES.join(', ')}. ` +
            `Falling back to ${LogLevel[fallback]}.`,
        );
        return fallback;
    }
    return level;
}
